/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { hasAtLeast, isEmpty, isNullish } from 'remeda'
import { useEventListener } from 'usehooks-ts'
import { ConditionalShopTheLook } from '@components/ConditionalShopTheLook/ConditionalShopTheLook'
import { lazyImport } from '@helpers'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import { useHeader } from '@velasca/hooks/use-header'
import type { ImageMedia, ModelMedia, VideoMedia } from '@velasca/types'
import { NavigationButton } from '@velasca/ui/navigation-button'
import { ZoomedImage } from '@velasca/ui/zoomed-image'
import { ZoomedVideo } from '@velasca/ui/zoomed-video'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { $settings } from '@velasca/utils/globals'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import withLazyLoad from '../LazyLoad/LazyLoad'
import {
  ContainerGrid,
  DefectiveMedia,
  GridElement,
  Image,
  ImageSkeleton,
  ImagesPageProductContainer,
  LeftArrowContainer,
  Model,
  RightArrowContainer,
  StyledScrollAreaScrollbar,
  StyledScrollAreaThumb,
  ThumbnailForSidebar,
  ThumbnailSidebarContainer,
  ThumbnailSidebarViewport,
  VideoContainer,
  VideoTile,
} from './ImagesPageProduct.styles'

const { SketchfabPlayer } = lazyImport(
  () => import('@velasca/ui/sketchfab-player'),
  'SketchfabPlayer',
)

const lazyConfig = {
  rootMargin: `800px 0px 800px 0px`,
  triggerOnce: true,
}

const SketchfabPlayerLazyLoad = withLazyLoad<
  React.ComponentProps<typeof SketchfabPlayer>
>((props) => {
  return (
    <Suspense fallback={null}>
      <SketchfabPlayer {...props} />
    </Suspense>
  )
}, lazyConfig)

const BIG_SIZES = [600, 750, 900, 1100, 1400, 1600]
const SMALL_SIZES = BIG_SIZES.map((e) => e / 2)

interface ImagesPageProductProps {
  medias: (ImageMedia | ModelMedia | VideoMedia)[]
  zoom?: boolean
  loading?: boolean
  scrollableImage?: boolean
  color?: string
  material?: string
  productTitle: string
  productType: string
  shopTheLookIDs?: string[]
  thumbnailSidebar?: boolean
  editorialImage?: string | null
}

export const ImagesPageProduct = ({
  medias,
  zoom = false,
  loading = false,
  scrollableImage,
  color = '',
  material = '',
  productTitle,
  productType,
  shopTheLookIDs,
  thumbnailSidebar = false,
  editorialImage,
}: ImagesPageProductProps) => {
  const { t } = useTranslation('product')

  const promotionalBannerHeight = useHeader(
    (state) => state.promotionalBannerHeight,
  )
  const navbarHeight = useHeader((state) => state.navbarHeight)

  const headerHeight = promotionalBannerHeight + navbarHeight

  const [zoomedImageIndex, setZoomedImageIndex] = useState<number | null>(null)
  const [isModelFullScreen, setIsModelFullScreen] = useState(false)
  const imageRefs = useRef<({ currentSrc: string } | null)[]>([])
  const { ref: vidRef, inView, entry } = useInView({ threshold: 0 })

  const currentMedia = useMemo(() => {
    if (zoomedImageIndex === null) {
      return null
    }

    return medias[zoomedImageIndex]
  }, [zoomedImageIndex, medias])

  const defaultModelWidth = useMemo(() => {
    const search = (
      medias?.find((media) => media?.type === 'image') as ImageMedia
    )?.width

    return search || 4
  }, [medias])

  const defaultModelHeight = useMemo(() => {
    const search = (
      medias?.find((media) => media?.type === 'image') as ImageMedia
    )?.height

    return search || 5
  }, [medias])

  const handlePrevImage = useCallback(() => {
    setZoomedImageIndex((index) => {
      // TODO: using the "non-null assertion operator" here is a trick. Currently the math operation still works even if the index is null, but this is not ideal, and should be refactored.
      return (index! + medias.length - 1) % medias.length
    })
  }, [setZoomedImageIndex, medias])

  const handleNextImage = useCallback(() => {
    setZoomedImageIndex((index) => {
      // TODO: using the "non-null assertion operator" here is a trick. Currently the math operation still works even if the index is null, but this is not ideal, and should be refactored.
      return (index! + 1) % medias.length
    })
  }, [setZoomedImageIndex, medias])

  const handleClose = useCallback(() => {
    setZoomedImageIndex(null)
    if (isModelFullScreen) {
      setIsModelFullScreen(false)
    }
  }, [setZoomedImageIndex, isModelFullScreen, setIsModelFullScreen])

  useEffect(() => {
    currentMedia?.type === 'model'
      ? setIsModelFullScreen(true)
      : setIsModelFullScreen(false)
  }, [currentMedia])

  useEffect(() => {
    if (!entry?.target) {
      return
    }

    const entryTarget = entry.target as HTMLVideoElement

    if (inView && !currentMedia) {
      entryTarget.play().catch((error) => {
        handleError(error)
      })
    } else {
      entryTarget.pause()
    }
  }, [entry, inView, currentMedia])

  const navigate = (e: KeyboardEvent) => {
    if (zoomedImageIndex === null) {
      return
    }

    switch (e.code) {
      case 'Escape': {
        handleClose()
        break
      }
      case 'ArrowRight': {
        handleNextImage()
        break
      }
      case 'ArrowLeft': {
        handlePrevImage()
        break
      }
      default: {
        return
      }
    }
  }

  useEventListener('keydown', navigate)

  const thumbnailsForSidebar = useMemo(() => {
    if (!thumbnailSidebar) {
      return []
    }

    return medias.map((media, index) => {
      const imageWidth = 90
      const imageHeight = 113

      if (media.type === 'model') {
        return null
      }
      if (loading) {
        return (
          <ThumbnailForSidebar key={`thumbnail-for-sidebar-${index}`}>
            <ImageSkeleton imageWidth={imageWidth} imageHeight={imageHeight} />
          </ThumbnailForSidebar>
        )
      }
      const mediaSrc = media?.type === 'image' ? media?.src : media.preview
      const mediaWidth =
        media.type === 'image'
          ? media?.width
          : (media.sources.at(0)?.width ?? imageWidth)

      return (
        <ThumbnailForSidebar
          key={`thumbnail-for-sidebar-${index}`}
          onClick={() => {
            // TODO: this should be made the react-way with refs
            const element = document.querySelector(
              `[data-element-index="${index}"]`,
            )

            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              })
            }
          }}
        >
          <Image
            lazyload
            lqip
            imageWidth={imageWidth}
            imageHeight={imageHeight}
            transitionDuration={50}
            imgSrc={setQueryParams(mediaSrc, [{ name: 'width', value: '30' }])}
            sources={buildIntrinsicWidthSources({
              src: mediaSrc,
              maxWidth: mediaWidth,
              sizes: SMALL_SIZES,
            })}
            alt={t('apparelProductPageImgAltTemplate', {
              color,
              material,
              productTitle,
              productType,
              imgNumber: index + 1,
            })}
            ref={(ref) => {
              imageRefs.current[index] = ref
            }}
          />
        </ThumbnailForSidebar>
      )
    })
  }, [
    color,
    loading,
    material,
    medias,
    productTitle,
    productType,
    t,
    thumbnailSidebar,
  ])

  if (isEmpty(medias)) {
    return null
  }

  return (
    <ImagesPageProductContainer thumbnailSidebar={thumbnailSidebar}>
      <ThumbnailSidebarContainer $headerHeight={headerHeight}>
        <ScrollArea.Root type="hover">
          <ScrollArea.Viewport>
            <ThumbnailSidebarViewport>
              {thumbnailsForSidebar}
            </ThumbnailSidebarViewport>
          </ScrollArea.Viewport>
          <StyledScrollAreaScrollbar orientation="vertical">
            <StyledScrollAreaThumb />
          </StyledScrollAreaScrollbar>
        </ScrollArea.Root>
      </ThumbnailSidebarContainer>
      <ContainerGrid>
        {medias?.map((media, index) => {
          const imageWidth =
            media?.type === 'image' && media?.width ? media.width : 4
          const imageHeight =
            media?.type === 'image' && media?.height ? media.height : 5

          // Layout requirement:
          // - First 4 large (span 2 columns)
          // - Then 2 small side-by-side
          // - Then all remaining large
          // Edge-case: if there are fewer than 6 medias, keep all large to avoid odd gaps.
          const hasTwoSmalls = medias.length >= 6
          const isInTwoSmallWindow =
            hasTwoSmalls && (index === 4 || index === 5)
          const isBigImage = index < 4 || !isInTwoSmallWindow

          if (loading) {
            return (
              <GridElement key={index} zoom={zoom} stretch={isBigImage}>
                <ImageSkeleton
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />
              </GridElement>
            )
          }

          return (
            <GridElement
              key={index}
              zoom={zoom}
              stretch={isBigImage}
              data-element-index={index}
              customZoomIconUrl={$settings.pdpZoomIconUrl}
            >
              <ErrorBoundary
                fallback={<DefectiveMedia>Media unavailable</DefectiveMedia>}
              >
                <ConditionalShopTheLook
                  shopTheLookIDs={shopTheLookIDs}
                  editorialImage={editorialImage}
                  condition={
                    index === 0 &&
                    Boolean(shopTheLookIDs && hasAtLeast(shopTheLookIDs, 1))
                  }
                >
                  {media?.type === 'model' && (
                    <Model
                      isFullScreen={isModelFullScreen}
                      width={defaultModelWidth}
                      height={defaultModelHeight}
                    >
                      <SketchfabPlayerLazyLoad
                        id="sketchfab-tile-player"
                        modelId={media?.id}
                        isModelFullScreen={isModelFullScreen}
                      />
                    </Model>
                  )}

                  {media?.type === 'video' && (
                    <VideoContainer
                      mediaWidth={media.sources[0].width}
                      mediaHeight={media.sources[0].height}
                      onClick={() => {
                        setZoomedImageIndex(index)
                      }}
                    >
                      {media?.sources && (
                        <VideoTile
                          loop
                          autoPlay
                          muted
                          playsInline
                          controls={false}
                          controlsList="nodownload"
                          ref={vidRef}
                          onContextMenu={(e) => {
                            // needed to prevent video download on right click.
                            e.preventDefault()

                            return false
                          }}
                        >
                          {media?.sources?.map(({ src, mimeType }) => (
                            <source key={src} src={src} type={mimeType} />
                          ))}
                        </VideoTile>
                      )}
                    </VideoContainer>
                  )}

                  {media?.type === 'image' && (
                    <div
                      onClick={() => {
                        setZoomedImageIndex(index)
                      }}
                    >
                      {media?.src && (
                        <Image
                          lazyload
                          lqip
                          imageWidth={imageWidth}
                          imageHeight={imageHeight}
                          transitionDuration={50}
                          imgSrc={setQueryParams(media?.src, [
                            { name: 'width', value: '30' },
                          ])}
                          sources={buildIntrinsicWidthSources({
                            src: media?.src,
                            maxWidth: media?.width,
                            sizes: isBigImage ? BIG_SIZES : SMALL_SIZES,
                          })}
                          alt={t('apparelProductPageImgAltTemplate', {
                            color,
                            material,
                            productTitle,
                            productType,
                            imgNumber: index + 1,
                          })}
                          ref={(ref) => {
                            imageRefs.current[index] = ref
                          }}
                        />
                      )}
                    </div>
                  )}
                </ConditionalShopTheLook>
              </ErrorBoundary>
            </GridElement>
          )
        })}

        {currentMedia && !isNullish(zoomedImageIndex) && (
          <>
            {(currentMedia?.type === 'image' && (
              <ZoomedImage
                zoomedImg={currentMedia}
                scrollableImage={scrollableImage}
                imgSrc={imageRefs?.current[zoomedImageIndex]?.currentSrc}
                onClick={handleClose}
              />
            )) ||
              (currentMedia?.type === 'video' && (
                <ZoomedVideo media={currentMedia} handleClose={handleClose} />
              ))}
          </>
        )}

        {zoom && !isNullish(zoomedImageIndex) && (
          <LeftArrowContainer>
            <NavigationButton buttonType="prev" onClick={handlePrevImage} />
          </LeftArrowContainer>
        )}
        {zoom && !isNullish(zoomedImageIndex) && (
          <RightArrowContainer>
            <NavigationButton buttonType="next" onClick={handleNextImage} />
          </RightArrowContainer>
        )}
      </ContainerGrid>
    </ImagesPageProductContainer>
  )
}
